import { t } from 'i18next';
import React from 'react';
import Icon from '../../../../../../components/content/icon';
import Button from '../../../../../../components/inputs/button';
import { Kind } from '../../../../../../components/inputs/button/constants/kind';
import LoadingWrapper from '../../../../../../components/utility/loading-wrapper';
import Placeholder from '../../../../../../components/utility/placeholder';
import COLORS from '../../../../../../constants/colors';
import { tKeys } from '../../../../../../translations/keys';
import useLogic from './logic.web';
import { Box, Checkbox, HStack, VStack, Text as ChakraText, Divider, useColorModeValue, Fade, ScaleFade, } from '@chakra-ui/react';
import CreateDocument from '../../../../more/repository/create-document/index.web';
import { Size } from '../../../../../../components/utility/loading/size';
import Dropzone from 'react-dropzone';
import { Trans } from 'react-i18next';
// Add custom CSS for animations
const customStyles = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
// Custom Modern Dropzone Component
const ModernDropzone = ({ isLoading, onDrop, }) => {
    const [isDragActive, setIsDragActive] = React.useState(false);
    const bgColor = useColorModeValue('gray.50', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const hoverBgColor = useColorModeValue('blue.50', 'blue.900');
    const hoverBorderColor = useColorModeValue('blue.300', 'blue.500');
    return (<Box w="full" px="1rem">
      <Dropzone onDrop={onDrop} onDragEnter={() => setIsDragActive(true)} onDragLeave={() => setIsDragActive(false)} disabled={isLoading}>
        {({ getRootProps, getInputProps }) => (<ScaleFade initialScale={0.95} in={true}>
            <Box {...getRootProps()} w="full" minH="120px" p="6" borderRadius="xl" border="2px" borderStyle="dashed" borderColor={isDragActive ? hoverBorderColor : borderColor} bg={isDragActive ? hoverBorderColor : bgColor} cursor="pointer" transition="all 0.2s ease-in-out" _hover={{
                bg: hoverBgColor,
                borderColor: hoverBorderColor,
                transform: 'translateY(-2px)',
                boxShadow: 'lg',
            }} display="flex" flexDirection="column" alignItems="center" justifyContent="center" position="relative" overflow="hidden">
              <input {...getInputProps()}/>

              {isLoading ? (<VStack spacing="3">
                  <Box w="12" h="12" borderRadius="full" border="3px solid" borderColor="blue.200" borderTopColor="blue.500" animation="spin 1s linear infinite"/>
                  <ChakraText fontSize="sm" color="gray.600">
                    {t(tKeys.common.loading)}...
                  </ChakraText>
                </VStack>) : (<VStack spacing="4">
                  <Box w="16" h="16" borderRadius="full" bg={isDragActive ? 'blue.100' : 'blue.50'} display="flex" alignItems="center" justifyContent="center" transition="all 0.2s ease-in-out">
                    <Icon name="picture-blue" width={32} height={32}/>
                  </Box>

                  <VStack spacing="2" textAlign="center" maxW="280px">
                    <ChakraText fontSize="md" fontWeight="semibold" color="gray.700">
                      {isDragActive
                    ? t(tKeys.event_details_files.dragzone.drop_here)
                    : t(tKeys.event_details_files.dragzone.drag_files)}
                    </ChakraText>

                    <ChakraText fontSize="sm" color="gray.500">
                      <Trans components={[
                    <Box as="span" key="1" color="blue.500" fontWeight="medium"/>,
                ]}>
                        {t(tKeys.agenda_item.documents.dragzone.browse_or_drag)}
                      </Trans>
                    </ChakraText>

                    <ChakraText fontSize="xs" color="gray.400">
                      {t(tKeys.event_details_files.dragzone.file_types)}
                    </ChakraText>
                  </VStack>
                </VStack>)}

              {/* Decorative elements */}
              <Box position="absolute" top="4" right="4" w="6" h="6" borderRadius="full" bg="blue.100" opacity="0.5"/>
              <Box position="absolute" bottom="4" left="4" w="4" h="4" borderRadius="full" bg="green.100" opacity="0.3"/>
            </Box>
          </ScaleFade>)}
      </Dropzone>
    </Box>);
};
// Modern Document Tile Component
const ModernDocumentTile = ({ document, index, isSelected, onSelect, onPress, onDelete, isAdmin, }) => {
    const getFileIcon = () => 'files';
    const getFileExtension = (filename) => {
        if (!filename)
            return '';
        return filename.split('.').pop()?.toUpperCase() || '';
    };
    return (<Fade in={true}>
      <Box px="4" py="1">
        <HStack spacing="3" alignItems="center" p="3" borderRadius="lg" bg="white" border="1px solid" borderColor="gray.200" _hover={{
            borderColor: 'blue.300',
            boxShadow: 'md',
            transform: 'translateY(-1px)',
        }} transition="all 0.2s ease-in-out" cursor="pointer" onClick={onPress}>
          {/* Checkbox */}
          <Box onClick={e => {
            e.stopPropagation();
            onSelect();
        }} p="1" cursor="pointer">
            <Checkbox isChecked={isSelected} onChange={() => { }} // Empty function since we handle click on the Box
     colorScheme="blue" size="md" pointerEvents="none"/>
          </Box>

          {/* Order number for agenda items */}

          {/* File type indicator */}
          <Box w="12" h="12" borderRadius="lg" bg="gray.50" border="1px solid" borderColor="gray.200" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <Icon name={getFileIcon()} width={16} height={16} fill={COLORS.Text.T500.value}/>
            {getFileExtension(document.file?.filename) && (<ChakraText fontSize="8px" fontWeight="bold" color="gray.500" lineHeight="1" mt="1">
                {getFileExtension(document.file?.filename)}
              </ChakraText>)}
          </Box>

          {/* Document info */}
          <VStack flex="1" align="start" spacing="1" overflow="hidden">
            <ChakraText fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1} w="full">
              {document?.title ?? document?.file?.filename}
            </ChakraText>

            {document.agendaItemTitle && (<ChakraText fontSize="xs" color="gray.600" noOfLines={1} w="full">
                {document.agendaItemTitle}
              </ChakraText>)}
          </VStack>

          {/* Actions */}
          {isAdmin && (<HStack spacing="2" onClick={e => e.stopPropagation()}>
              <Button kind={Kind.OUTLINE} size={Size.MINI} needConfirmation={true} onPress={onDelete}>
                <Icon name="cross" height={12} width={12} fill={COLORS.Text.T500.value}/>
              </Button>
            </HStack>)}
        </HStack>
      </Box>
    </Fade>);
};
// Document Section Component
const DocumentSection = ({ title, documents, onSelectDocument, selectedDocuments, onPressDocument, onDeleteDocument, isAdmin, }) => {
    if (!documents.length)
        return null;
    return (<VStack w="full" spacing="3" align="stretch">
      <HStack px="4" py="3" bg="gray.50" borderRadius="lg" mx="4">
        <Box w="1" h="6" bg="blue.500" borderRadius="full"/>
        <ChakraText fontSize="md" fontWeight="semibold" color="gray.700">
          {title}
        </ChakraText>
        <ChakraText fontSize="sm" color="gray.500" bg="gray.200" px="2" py="1" borderRadius="full">
          {documents.length}
        </ChakraText>
      </HStack>

      <VStack spacing="1" align="stretch">
        {documents.map((item, index) => (<ModernDocumentTile key={item.id} document={item} index={index} isSelected={selectedDocuments.includes(item.id)} onSelect={() => onSelectDocument(item.id)} onPress={() => onPressDocument(item.id)} onDelete={() => onDeleteDocument(item.id)} isAdmin={isAdmin}/>))}
      </VStack>
    </VStack>);
};
const EventDetailsFiles = ({ 
// eslint-disable-next-line react/prop-types
event, 
// eslint-disable-next-line react/prop-types
isAdmin, }) => {
    // Add custom styles to document head
    React.useEffect(() => {
        const styleSheet = document.createElement('style');
        styleSheet.innerText = customStyles;
        document.head.appendChild(styleSheet);
        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);
    // Attributes
    const { documents, isDocumentLoading, handleCreateDocument, handleDeleteDocument, handleOnPressDocument, handleOnDrop, selectedFile, isOpenCreateDocument, isCreateDocumentLoading, onCloseCreateDocument, refetchDocs, isLoadingDeleteDocument, handleOnSelectDocument, selectedDocuments, handleDownloadBundle, isLoadingCreateBundle, canBeDownloaded, } = useLogic(event);
    // Organize documents by category
    const organizedDocuments = React.useMemo(() => {
        if (!documents)
            return { eventDocuments: [], agendaDocuments: [] };
        const eventDocuments = [];
        const agendaDocuments = [];
        documents.forEach(category => {
            if (category.items) {
                category.items.forEach(doc => {
                    if (doc.agendaItemTitle) {
                        agendaDocuments.push(doc);
                    }
                    else {
                        eventDocuments.push(doc);
                    }
                });
            }
        });
        // Smart document name comparison function
        const compareDocumentNames = (docA, docB) => {
            const nameA = docA?.title ?? docA?.file?.filename ?? '';
            const nameB = docB?.title ?? docB?.file?.filename ?? '';
            // Extract leading numbers if they exist
            const numberMatchA = nameA.match(/^(\d+)/);
            const numberMatchB = nameB.match(/^(\d+)/);
            // If both start with numbers, compare numerically
            if (numberMatchA && numberMatchB) {
                const numA = parseInt(numberMatchA[1], 10);
                const numB = parseInt(numberMatchB[1], 10);
                if (numA !== numB) {
                    return numA - numB;
                }
                // If numbers are equal, compare the rest of the string
                return nameA.localeCompare(nameB);
            }
            // If only one starts with a number, number comes first
            if (numberMatchA && !numberMatchB)
                return -1;
            if (!numberMatchA && numberMatchB)
                return 1;
            // Both don't start with numbers, normal alphabetical sort
            return nameA.localeCompare(nameB);
        };
        // Sort all documents by name only
        eventDocuments.sort(compareDocumentNames);
        agendaDocuments.sort(compareDocumentNames);
        return { eventDocuments, agendaDocuments };
    }, [documents]);
    const totalDocuments = organizedDocuments.eventDocuments.length +
        organizedDocuments.agendaDocuments.length;
    // Functions
    const ListHeaderComponent = () => {
        if (!totalDocuments)
            return null;
        return (<VStack w="full" py="5" spacing="4">
        {isAdmin && (<ModernDropzone isLoading={isCreateDocumentLoading} onDrop={handleOnDrop}/>)}
      </VStack>);
    };
    function ListEmptyComponent() {
        if (isDocumentLoading)
            return null;
        return (<VStack w="full" py="8" spacing="6">
        <Placeholder imageType="monocle-emoji" hasImage={false} title={t(tKeys.calendar.files.placeholder.title)} subTitle={isAdmin
                ? t(tKeys.calendar.files.placeholder.subTitle)
                : t(tKeys.calendar.files.placeholder.subTitle_invitees)}/>
      </VStack>);
    }
    // Render
    return (<Box width="full" pb="8" overflow="auto">
      <LoadingWrapper isLoading={isDocumentLoading || isLoadingDeleteDocument}>
        <VStack spacing="6" align="stretch">
          {/* Header with integrated dropzone */}
          {isAdmin ? (<Dropzone onDrop={handleOnDrop} disabled={isCreateDocumentLoading}>
              {({ getRootProps, getInputProps, isDragActive }) => (<Box px="4" width="full">
                  <HStack {...getRootProps()} justifyContent="space-between" alignItems="center" px="4" py="3" width="full" bg={isDragActive ? 'blue.50' : 'blue.25'} borderRadius="lg" border="2px" borderStyle="dashed" borderColor={isDragActive ? 'blue.400' : 'blue.200'} cursor="pointer" transition="all 0.2s ease-in-out" _hover={{
                    bg: isDragActive ? 'blue.100' : 'blue.50',
                    borderColor: isDragActive ? 'blue.500' : 'blue.300',
                    transform: 'translateY(-1px)',
                    boxShadow: 'md',
                }} minH="60px">
                    <input {...getInputProps()}/>

                    {/* Left side - File info */}
                    <HStack spacing="3" flex="1" minW="0">
                      {isCreateDocumentLoading ? (<Box w="5" h="5" borderRadius="full" border="2px solid" borderColor="blue.200" borderTopColor="blue.500" animation="spin 1s linear infinite"/>) : (<Icon name="files" width={20} height={20} fill={COLORS.Govrn.Blue.T500.value}/>)}
                      <VStack align="start" spacing="0" flex="1" minW="0">
                        <HStack spacing="2" alignItems="center">
                          <ChakraText fontSize="md" fontWeight="semibold" color="blue.700" noOfLines={1}>
                            {isCreateDocumentLoading
                    ? t(tKeys.event_details_files.header.uploading)
                    : isDragActive
                        ? t(tKeys.event_details_files.dragzone.drop_here)
                        : t(tKeys.event_details_files.header.files)}
                          </ChakraText>
                          {!isDragActive && !isCreateDocumentLoading && (<ChakraText fontSize="10px" color="blue.500" bg="blue.100" px="2" py="0.5" borderRadius="full" fontWeight="medium" flexShrink={0}>
                              {t(tKeys.event_details_files.header.zone_label)}
                            </ChakraText>)}
                        </HStack>
                        <ChakraText fontSize="xs" color="blue.600" noOfLines={1}>
                          {isCreateDocumentLoading
                    ? t(tKeys.event_details_files.header.waiting)
                    : isDragActive
                        ? t(tKeys.event_details_files.header.added)
                        : t(tKeys.event_details_files.header
                            .documents_count, { count: totalDocuments })}
                        </ChakraText>
                      </VStack>
                    </HStack>

                    {/* Right side - Actions */}
                    <HStack spacing="3" flexShrink={0}>
                      {!isDragActive &&
                    canBeDownloaded &&
                    totalDocuments > 0 && (<Box onClick={e => {
                        e.stopPropagation();
                    }}>
                            <Button onPress={handleDownloadBundle} isLoading={isLoadingCreateBundle} isDisabled={!selectedDocuments.length} kind={Kind.OUTLINE} size={Size.COMPACT}>
                              <HStack spacing="1">
                                <Icon name="downloaded" width={14} height={14}/>
                                <ChakraText fontSize="xs">
                                  {t(tKeys.event_details_files.download_bundle
                        .button, { count: selectedDocuments.length })}
                                </ChakraText>
                              </HStack>
                            </Button>
                          </Box>)}
                    </HStack>
                  </HStack>
                </Box>)}
            </Dropzone>) : (
        // Non-admin header (no dropzone)
        <HStack justifyContent="space-between" alignItems="center" px="4" py="4" width="full" bg="gray.50" borderRadius="lg" mx="4">
              <HStack spacing="3">
                <Icon name="files" width={20} height={20} fill={COLORS.Text.T500.value}/>
                <VStack align="start" spacing="0">
                  <ChakraText fontSize="lg" fontWeight="semibold" color="gray.700">
                    {t(tKeys.event_details_files.header.files)}
                  </ChakraText>
                  <ChakraText fontSize="sm" color="gray.500">
                    {t(tKeys.event_details_files.header.documents_count, {
                count: totalDocuments,
            }).split(' • ')[0]}
                  </ChakraText>
                </VStack>
              </HStack>

              {canBeDownloaded && totalDocuments > 0 && (<Button onPress={handleDownloadBundle} isLoading={isLoadingCreateBundle} isDisabled={!selectedDocuments.length} kind={Kind.OUTLINE}>
                  <HStack spacing="2">
                    <Icon name="downloaded" width={16} height={16}/>
                    <ChakraText fontSize="sm">
                      {t(tKeys.common.download)} ({selectedDocuments.length})
                    </ChakraText>
                  </HStack>
                </Button>)}
            </HStack>)}

          {/* Content */}
          {totalDocuments > 0 ? (<VStack spacing="8" align="stretch">
              {/* Event Documents */}
              <DocumentSection title={t(tKeys.event_details_files.section.event_documents)} documents={organizedDocuments.eventDocuments} onSelectDocument={handleOnSelectDocument} selectedDocuments={selectedDocuments} onPressDocument={handleOnPressDocument} onDeleteDocument={handleDeleteDocument} isAdmin={isAdmin}/>

              {/* Divider */}
              {organizedDocuments.eventDocuments.length > 0 &&
                organizedDocuments.agendaDocuments.length > 0 && <Divider />}

              {/* Agenda Item Documents */}
              <DocumentSection title={t(tKeys.event_details_files.section.agenda_documents)} documents={organizedDocuments.agendaDocuments} onSelectDocument={handleOnSelectDocument} selectedDocuments={selectedDocuments} onPressDocument={handleOnPressDocument} onDeleteDocument={handleDeleteDocument} isAdmin={isAdmin} showOrder={true}/>
            </VStack>) : (<ListEmptyComponent />)}
        </VStack>
      </LoadingWrapper>

      {/* Modal */}
      <CreateDocument isOpen={isOpenCreateDocument} onClose={onCloseCreateDocument} selectedFile={selectedFile} refetchDocuments={refetchDocs}/>
    </Box>);
};
export default EventDetailsFiles;
